import React, { useCallback, useEffect, useMemo, useState } from "react";
import Segmented from "@/components/Segmented";
import { Loader2, TriangleAlert, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BinList } from "./WarehouseActions";
import MoveBinDialog from "./MoveBinDialog";

const STATUS_OPTIONS = [["", "Everything"], ["red", "Red — scheduled and short"],
  ["amber", "Amber — on hand, nobody asked"], ["short", "Short (any)"],
  ["not_ordered", "Not ordered"], ["on_site", "On site"], ["surplus", "Surplus"],
  ["installed", "Installed"]];
const LEVELS = [["by_room", "Room"], ["by_area", "Area"], ["by_floor", "Floor"],
  ["by_scope", "Scope"], ["products", "Product"]];
const ROLL_COLS = [["demand", "Demand"], ["inbound", "Inbound"], ["requested", "Requested"],
  ["pulled", "Pulled"], ["on_site", "On site"], ["installed", "Installed"],
  ["returned", "Returned"], ["not_on_hand", "Not on hand"], ["not_ordered", "Not ordered"],
  ["surplus", "Surplus"]];

const n = (v) => (Number(v || 0) ? Number(v).toLocaleString() : "");
const FLAG_ROW = { red: "bg-red-50/70", amber: "bg-amber-50/60" };

const Cell = ({ v, tone = "" }) => (
  <td className={`px-2 py-1 text-right tabular-nums ${Number(v || 0) ? tone : "text-gray-300"}`}>
    {n(v) || "·"}
  </td>
);

/** FLD-6 — room × product. Every number is derived; nothing here is entered. */
export default function MaterialStatusGrid({ projectId }) {
  const [data, setData] = useState(null);
  const [level, setLevel] = useState("by_room");
  const [f, setF] = useState({ scope: "", floor: "", status: "", scheduled_only: false,
                               co_only: false });
  const [allRed, setAllRed] = useState(false);
  const [moveBin, setMoveBin] = useState(null);   // product row → P3-4 move stock between bins

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/material/status",
        { params: { project_id: projectId, ...f } });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not read the material status");
    }
  }, [projectId, f]);
  useEffect(() => { load(); }, [load]);

  const rollup = useMemo(() => (data ? data[level] || [] : []), [data, level]);

  if (!data) {
    return <div className="p-6 text-center" data-testid="material-status-loading">
      <Loader2 className="w-4 h-4 animate-spin inline" />
    </div>;
  }
  const t = data.totals;

  return (
    <div className="space-y-2" data-testid="material-status-grid">
      {moveBin && <MoveBinDialog projectId={projectId} product={moveBin} onClose={() => setMoveBin(null)} onMoved={load} />}
      <div className="flex flex-wrap items-center gap-2">
        <select value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })}
                data-testid="status-filter-scope"
                className="h-7 text-xs border border-gray-300 rounded px-2 bg-white">
          <option value="">All scopes</option>
          {data.scopes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={f.floor} onChange={(e) => setF({ ...f, floor: e.target.value })}
                data-testid="status-filter-floor"
                className="h-7 text-xs border border-gray-300 rounded px-2 bg-white">
          <option value="">All floors</option>
          {data.floors.map((s) => <option key={s} value={s}>Floor {s}</option>)}
        </select>
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
                data-testid="status-filter-status"
                className="h-7 text-xs border border-gray-300 rounded px-2 bg-white">
          {STATUS_OPTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <label className="text-xs flex items-center gap-1 text-gray-700">
          <input type="checkbox" checked={f.scheduled_only} data-testid="status-filter-scheduled"
                 onChange={(e) => setF({ ...f, scheduled_only: e.target.checked })} />
          Scheduled only
        </label>
        <label className="text-xs flex items-center gap-1 text-gray-700">
          <input type="checkbox" checked={f.co_only} data-testid="status-filter-co"
                 onChange={(e) => setF({ ...f, co_only: e.target.checked })} />
          Change orders only
        </label>
        <span className="ml-auto flex items-center gap-2 text-[11px]">
          <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200"
                data-testid="status-red-count">{t.red} red</span>
          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200"
                data-testid="status-amber-count">{t.amber} amber</span>
          <span className="text-gray-500">scheduled inside {data.horizon_days} day(s)</span>
        </span>
      </div>

      {data.red_rooms.length > 0 && (
        <div className="border border-red-300 bg-red-50 rounded-lg p-2" data-testid="status-red-rooms">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-red-800 flex items-center gap-1">
            <TriangleAlert className="w-3.5 h-3.5" /> Rooms that will be walked into without material
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {(allRed ? data.red_rooms : data.red_rooms.slice(0, 8)).map((r) => (
              <span key={r.room_key} data-testid={`status-red-room-${r.room_key}`}
                    className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-red-200 text-red-900">
                {r.name} · starts {r.start || "—"} · {n(r.not_on_hand + r.not_ordered)} short
              </span>
            ))}
            {data.red_rooms.length > 8 && (
              <button onClick={() => setAllRed((v) => !v)} data-testid="status-red-rooms-toggle"
                      className="text-[11px] text-red-800 underline underline-offset-2">
                {allRed ? "show fewer" : `+${data.red_rooms.length - 8} more`}
              </button>
            )}
          </div>
        </div>
      )}

      <div data-testid="status-rollup-levels">
        <Segmented
          options={LEVELS.map(([k, label]) => ({ key: k, label, testId: `status-level-${k}` }))}
          value={level}
          onChange={setLevel}
        />
      </div>

      <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="status-rollup-table">
          <thead className="text-[10px] uppercase text-gray-500 bg-gray-50">
            <tr>
              <th className="px-2 py-1 text-left">
                {level === "products" ? "Product" : LEVELS.find(([k]) => k === level)[1]}
              </th>
              {level === "products" && <th className="px-2 py-1 text-right">On hand</th>}
              {ROLL_COLS.map(([k, label]) => (
                <th key={k} className="px-2 py-1 text-right">{label}</th>
              ))}
              <th className="px-2 py-1 text-right">Flags</th>
            </tr>
          </thead>
          <tbody>
            {rollup.map((r, i) => (
              <tr key={r.room_key || r.product_uid || r.name || i}
                  data-testid={`status-rollup-row-${r.room_key || r.product_uid || r.name}`}
                  className={`border-t border-gray-100 ${r.red > 0 ? FLAG_ROW.red
                    : r.amber > 0 ? FLAG_ROW.amber : ""}`}>
                <td className="px-2 py-1 font-medium whitespace-nowrap">
                  {r.name || r.product_short || "—"}
                  {r.start && <span className="ml-1 text-gray-400">starts {r.start}</span>}
                </td>
                {level === "products" && (
                  <td className="px-2 py-1 text-right tabular-nums font-semibold text-gray-900"
                      data-testid={`status-on-hand-${r.product_uid}`}>
                    {n(r.on_hand) || "0"}
                    {/* FT-2 §7 / P3-4 — where those on-hand units live, every bin with its count; move between bins */}
                    <BinList bins={r.bins} testId={`status-bin-${r.product_uid}`} className="font-normal" />
                    {(r.bins || []).length > 0 && (
                      <button type="button" onClick={() => setMoveBin(r)} data-testid={`status-move-bin-${r.product_uid}`}
                              className="text-[10px] font-normal text-sky-700 hover:underline dark:text-sky-400">Move bin</button>
                    )}
                  </td>
                )}
                {ROLL_COLS.map(([k]) => <Cell key={k} v={r[k]} tone={
                  k === "not_on_hand" || k === "not_ordered" ? "text-red-700 font-semibold"
                    : k === "on_site" ? "text-emerald-700" : "text-gray-800"} />)}
                <td className="px-2 py-1 text-right">
                  {r.red > 0 && <span className="text-red-700 font-semibold">{r.red}R </span>}
                  {r.amber > 0 && <span className="text-amber-800 font-semibold">{r.amber}A</span>}
                </td>
              </tr>
            ))}
            {rollup.length === 0 && (
              <tr><td colSpan={13} className="px-2 py-6 text-center text-gray-500">
                Nothing matches those filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
          Room × product — {data.cells.length} line(s)
        </div>
        <table className="w-full text-[11px]" data-testid="status-cell-table">
          <thead className="text-[10px] uppercase text-gray-500 bg-gray-50">
            <tr>
              <th className="px-2 py-1 text-left sticky left-0 bg-gray-50 z-10">Room</th>
              <th className="px-2 py-1 text-left">Scope</th>
              <th className="px-2 py-1 text-left">Product</th>
              <th className="px-2 py-1 text-left">Starts</th>
              <th className="px-2 py-1 text-right">Demand</th>
              <th className="px-2 py-1 text-right">Inbound</th>
              <th className="px-2 py-1 text-right">Requested</th>
              <th className="px-2 py-1 text-right">Pulled</th>
              <th className="px-2 py-1 text-right">On site</th>
              <th className="px-2 py-1 text-left">Where</th>
              <th className="px-2 py-1 text-right">Installed</th>
              <th className="px-2 py-1 text-right">Returned</th>
              <th className="px-2 py-1 text-right">Not on hand</th>
              <th className="px-2 py-1 text-right">Not ordered</th>
            </tr>
          </thead>
          <tbody>
            {data.cells.map((c) => (
              <tr key={c.key} data-testid={`status-cell-${c.key}`} data-flag={c.flag || "none"}
                  className={`border-t border-gray-100 ${FLAG_ROW[c.flag] || ""}`}>
                <td className={`px-2 py-1 font-medium whitespace-nowrap sticky left-0 z-10 ${
                  c.flag === "red" ? "bg-red-50" : c.flag === "amber" ? "bg-amber-50" : "bg-white"}`}>
                  {c.flag === "red" && <TriangleAlert className="w-3 h-3 inline mr-1 text-red-700" />}
                  {c.room_label || c.room_key}
                </td>
                <td className="px-2 py-1 text-gray-600 whitespace-nowrap">{c.scope_code || c.scope_type}</td>
                <td className="px-2 py-1 whitespace-nowrap" title={c.product_description}>
                  {c.product_short || c.product_part_number || "—"}
                  {c.is_change_order && (
                    <span className="ml-1 text-[9px] uppercase px-1 rounded bg-violet-100 text-violet-800">CO</span>
                  )}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-600">
                  {c.start ? (
                    <span className={c.scheduled ? "text-gray-900 font-medium" : ""}>
                      <Clock className="w-3 h-3 inline mr-0.5 text-gray-400" />{c.start}
                      {c.start_source === "mobilization" && (
                        <span className="ml-1 text-[9px] uppercase text-blue-700">dispatch</span>
                      )}
                    </span>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <Cell v={c.demand} tone="text-gray-900 font-medium" />
                <td className="px-2 py-1 text-right tabular-nums">
                  {c.inbound > 0 ? (
                    <span title={`${c.po_ref || "PO"} · ETA ${c.eta || "unknown"}`
                                 + (c.direct_ship ? " · ships straight to site" : "")}
                          className="text-sky-800">
                      {n(c.inbound)}{c.eta ? ` · ${c.eta.slice(5)}` : ""}
                      {c.direct_ship && (
                        <span data-testid={`status-direct-ship-${c.key}`}
                              className="ml-1 text-[9px] uppercase font-semibold px-1 rounded bg-sky-100 text-sky-800">
                          ds
                        </span>
                      )}
                    </span>
                  ) : <span className="text-gray-300">·</span>}
                </td>
                <Cell v={c.requested} tone="text-gray-800" />
                <Cell v={c.pulled} tone="text-amber-800" />
                <Cell v={c.on_site} tone="text-emerald-700 font-semibold" />
                <td className="px-2 py-1 text-gray-600 max-w-[180px] truncate"
                    title={(c.locations || []).join(", ")}>
                  {c.locations?.length ? (
                    <><MapPin className="w-3 h-3 inline mr-0.5 text-emerald-700" />
                      {c.locations.join(", ")}</>
                  ) : <span className="text-gray-300">·</span>}
                </td>
                <Cell v={c.installed} tone="text-blue-700" />
                <Cell v={c.returned} tone="text-gray-700" />
                <Cell v={c.not_on_hand} tone="text-red-700 font-bold" />
                <Cell v={c.not_ordered} tone="text-red-700 font-bold" />
              </tr>
            ))}
            {data.cells.length === 0 && (
              <tr><td colSpan={14} className="px-2 py-6 text-center text-gray-500">
                Nothing matches those filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-gray-500">
        Red = the room is scheduled inside {data.horizon_days} day(s) and something is not on hand
        or not ordered. Amber = it is on hand and nobody has asked for it yet. On hand is a project
        ledger per product, so it is shown on the Product rollup.
      </p>
    </div>
  );
}
